"use client"

import { useState, useEffect } from "react"
import { getDeviceFingerprint } from "@/lib/fingerprint"

type Status = "idle" | "sending" | "sent" | "device_pending" | "device_approval_required" | "error"

export default function LoginPage() {
  const [email, setEmail] = useState("")
  const [status, setStatus] = useState<Status>("idle")
  const [errorMsg, setErrorMsg] = useState<string>("")
  const [fingerprint, setFingerprint] = useState<string>("")
  const [trialStarted, setTrialStarted] = useState(false)
  const [trialDays, setTrialDays] = useState(14)

  useEffect(() => {
    getDeviceFingerprint().then(setFingerprint).catch(() => setFingerprint(""))
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = email.trim().toLowerCase()
    if (!trimmed || !trimmed.includes("@")) {
      setErrorMsg("Enter a valid email address")
      setStatus("error")
      return
    }
    if (!fingerprint) {
      setErrorMsg("Initializing device — please wait a moment and try again")
      setStatus("error")
      return
    }
    setStatus("sending")
    setErrorMsg("")
    try {
      const res = await fetch("/api/auth/request-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, fingerprint }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.status === 200 && data.status === "magic_link_sent") {
        setTrialStarted(Boolean(data.trial_started))
        setTrialDays(Number(data.trial_days) || 14)
        setStatus("sent")
      } else if (res.status === 202 && data.status === "device_approval_required") {
        setStatus("device_approval_required")
      } else if (res.status === 202 && data.status === "device_approval_pending") {
        setStatus("device_pending")
      } else {
        setErrorMsg(data.error || "Something went wrong. Please try again.")
        setStatus("error")
      }
    } catch (err: any) {
      setErrorMsg(err?.message || "Network error")
      setStatus("error")
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Brand header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white font-bold text-sm">
              N
            </div>
            <span className="text-xl font-semibold text-white tracking-tight">NairAlpha</span>
          </div>
          <div className="text-[11px] uppercase tracking-[2px] text-blue-400 font-semibold">
            Nigerian Market Intelligence
          </div>
        </div>

        {/* Card */}
        <div className="bg-[#0f1420] border border-[#1f2937] rounded-2xl p-8 shadow-2xl">
          {status === "sent" ? (
            <SuccessState
              email={email}
              trialStarted={trialStarted}
              trialDays={trialDays}
              onReset={() => { setStatus("idle"); setEmail(""); setTrialStarted(false) }}
            />
          ) : status === "device_approval_required" ? (
            <DeviceApprovalSent email={email} />
          ) : status === "device_pending" ? (
            <DevicePending email={email} />
          ) : (
            <>
              <h1 className="text-2xl font-semibold text-white mb-2">Sign in</h1>
              <p className="text-gray-400 text-sm mb-6">
                We'll email you a one-time link — no password required.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-xs uppercase tracking-wider text-gray-500 mb-2 font-semibold">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={status === "sending"}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 bg-[#0a0e1a] border border-[#1f2937] rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-blue-500 transition-colors disabled:opacity-50"
                  />
                </div>
                {status === "error" && (
                  <div className="text-sm text-red-400 bg-red-950/30 border border-red-900/50 rounded-lg px-3 py-2">
                    {errorMsg}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={status === "sending" || !fingerprint}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-900 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors text-sm"
                >
                  {status === "sending" ? "Sending link..." : "Email me a sign-in link →"}
                </button>
              </form>
              <div className="mt-6 pt-6 border-t border-[#1f2937] text-center">
                <p className="text-xs text-gray-500">
                  Don't have an account?{" "}
                  <a href="/?tab=pricing" className="text-blue-400 hover:text-blue-300">
                    View pricing
                  </a>
                </p>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-gray-600 mt-6">
          Protected by device-binding. Admin accounts require per-device approval.
        </p>
      </div>
    </div>
  )
}

function SuccessState({
  email,
  trialStarted,
  trialDays,
  onReset,
}: {
  email: string
  trialStarted: boolean
  trialDays: number
  onReset: () => void
}) {
  return (
    <div className="text-center">
      <div className="w-14 h-14 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mx-auto mb-4">
        <svg className="w-7 h-7 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-white mb-2">Check your email</h2>
      <p className="text-gray-400 text-sm mb-6">
        We sent a sign-in link to <span className="text-white font-medium">{email}</span>.
        Click the link to continue — it expires in 15 minutes.
      </p>

      {trialStarted && (
        <div className="text-sm text-blue-100 bg-blue-950/40 border border-blue-700/50 rounded-lg p-4 mb-4 text-left">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
            <div className="font-semibold text-white">Your {trialDays}-day free trial is starting</div>
          </div>
          <p className="text-xs text-blue-200/80">
            You'll get full <b>Professional</b> access — all 33 stock rankings,
            Factor Lab, Intelligence briefs, and Portfolio tracking — free for
            {" "}{trialDays} days. No credit card required.
          </p>
        </div>
      )}

      <div className="text-xs text-gray-500 bg-[#0a0e1a] border border-[#1f2937] rounded-lg p-3 mb-4">
        Can't find it? Check your spam folder, then mark as "not spam" so future emails land in your inbox.
      </div>
      <button onClick={onReset} className="text-sm text-blue-400 hover:text-blue-300">
        ← Use a different email
      </button>
    </div>
  )
}

function DeviceApprovalSent({ email }: { email: string }) {
  return (
    <div className="text-center">
      <div className="w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-4">
        <svg className="w-7 h-7 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-white mb-2">New device detected</h2>
      <p className="text-gray-400 text-sm mb-6">
        Because <span className="text-white font-medium">{email}</span> is an admin account, this device needs to be approved before it can sign in.
      </p>
      <div className="text-sm text-amber-200 bg-amber-950/30 border border-amber-900/50 rounded-lg p-4 text-left space-y-2">
        <p className="font-semibold">What to do next:</p>
        <ol className="list-decimal list-inside space-y-1 text-xs text-amber-100/80">
          <li>Open your email on a device that's already trusted</li>
          <li>Find the "New device wants access" email</li>
          <li>Click "Approve this device"</li>
          <li>Come back here and try signing in again</li>
        </ol>
      </div>
    </div>
  )
}

function DevicePending({ email }: { email: string }) {
  return (
    <div className="text-center">
      <div className="w-14 h-14 rounded-full bg-gray-500/10 border border-gray-500/30 flex items-center justify-center mx-auto mb-4">
        <svg className="w-7 h-7 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-white mb-2">Approval pending</h2>
      <p className="text-gray-400 text-sm">
        This device is waiting to be approved. Check the approval email we sent to <span className="text-white font-medium">{email}</span> from a trusted device.
      </p>
    </div>
  )
}
